import axios from 'axios';

// PATCH: lista de domínios LEGADOS que NUNCA podem ser usados em produção.
// Em produção, o bundle pode estar compilado com REACT_APP_BACKEND_URL apontando
// para um domínio velho (ex.: dnahub-school-1.emergent.host). Em vez de respeitar
// esse env stale, usamos `window.location.origin` (mesma origem do navegador) —
// funciona em preview, prod, e qualquer domínio futuro.
const _BLOCKED_BACKEND_HOSTS = [
  'dnahub-school-1.emergent.host',
  'localhost',
  '127.0.0.1',
];
function _resolveApiBaseUrl() {
  // 1) Origem do navegador é a fonte de verdade (sempre correta)
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin.replace(/\/+$/, '') + '/api';
  }
  // 2) Fallback (SSR/teste): env var, descartando legacy
  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (raw) {
    try {
      const u = new URL(raw);
      if (!_BLOCKED_BACKEND_HOSTS.some((h) => u.hostname.includes(h))) {
        return raw.replace(/\/+$/, '') + '/api';
      }
    } catch (_) { /* noop */ }
  }
  return '/api';
}

const API_URL = _resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000, // 60 segundos de timeout (aumentado para operações mais pesadas)
});

// Configuração de retry para requisições
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 segundo

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Adicionar timestamp para evitar cache
  if (config.method === 'get') {
    config.params = { ...config.params, _t: Date.now() };
  }
  return config;
});

// Handle errors with retry logic
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Se for 401, redirecionar para login (apenas contexto admin, nao familia)
    if (error.response?.status === 401) {
      const isFamiliaRoute = ['/familia', '/autorizacao-evento'].some((p) => window.location.pathname.startsWith(p));
      if (!isFamiliaRoute) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
    
    // ── Normalização do erro (request_id ponta a ponta + mensagem controlada) ──
    const resp = error.response;
    const data = resp?.data;
    const isHtml = typeof data === 'string' && /<html|cloudflare/i.test(data);
    error.requestId = resp?.headers?.['x-request-id'] || (data && typeof data === 'object' ? data.request_id : null) || null;
    error.status = resp?.status || 0;
    if (!resp) {
      error.userMessage = error.code === 'ECONNABORTED'
        ? 'O servidor demorou para responder. A operação pode continuar em processamento.'
        : 'Sem conexão com o servidor. Verifique sua rede e tente novamente.';
    } else if (isHtml || [502, 503, 504, 520, 521, 522, 523, 524].includes(resp.status)) {
      error.userMessage = `Servidor temporariamente indisponível (HTTP ${resp.status}).`;
      error.gateway = true;
    } else {
      const d = data && typeof data === 'object' ? data : {};
      error.userMessage = (typeof d.message === 'string' && d.message)
        || (typeof d.detail === 'string' && d.detail)
        || 'Não foi possível concluir esta operação.';
    }
    if (error.requestId) error.userMessage += ` Código de referência: ${error.requestId}`;

    // ── Retry SOMENTE em leituras idempotentes e só para falhas de rede/gateway (nunca 500, nunca POST) ──
    const method = (config?.method || 'get').toLowerCase();
    const idempotent = ['get', 'head', 'options'].includes(method) && !config?.__noRetry;
    const transient = !resp || [502, 503, 504, 520, 521, 522, 523, 524].includes(resp.status);
    if (!config.__retryCount) config.__retryCount = 0;
    if (idempotent && transient && config.__retryCount < MAX_RETRIES) {
      config.__retryCount += 1;
      await sleep(RETRY_DELAY * config.__retryCount);
      return api(config);
    }
    return Promise.reject(error);
  }
);

/** Mensagem amigável + request_id para exibir em toasts/telas. */
export const errorMessage = (e, fallback = 'Não foi possível concluir esta operação.') =>
  e?.userMessage || e?.response?.data?.detail || e?.response?.data?.message || fallback;

export default api;

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
};

// Students API
export const studentsAPI = {
  list: () => api.get('/students'),
  get: (id) => api.get(`/students/${id}`),
  create: (data) => api.post('/students', data),
  update: (id, data) => api.put(`/students/${id}`, data),
  delete: (id) => api.delete(`/students/${id}`),
};

// Responsibles API
export const responsiblesAPI = {
  list: () => api.get('/responsibles'),
  get: (id) => api.get(`/responsibles/${id}`),
  create: (data) => api.post('/responsibles', data),
  update: (id, data) => api.put(`/responsibles/${id}`, data),
};

// Titles API
export const titlesAPI = {
  list: (params) => api.get('/titles', { params }),
  create: (data) => api.post('/titles', data),
  generateMonthly: (data) => api.post('/titles/generate-monthly', data),
  recalculateCharges: (id) => api.post(`/titles/${id}/recalculate-charges`),
  recalculateAll: () => api.post('/titles/recalculate-all-charges'),
  markAsPaid: (id, paidValue, paymentDate) => {
    const formData = new FormData();
    formData.append('paid_value', paidValue);
    if (paymentDate) formData.append('payment_date', paymentDate);
    return api.post(`/titles/${id}/mark-as-paid`, formData);
  },
  markAsPaidWithReceipt: (id, data) => api.post(`/titles/${id}/mark-as-paid-with-receipt`, data),
};

// Bank API
export const bankAPI = {
  processReturn: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bank/process-return', formData);
  },
  generateRemittance: (titleIds) => {
    const formData = new FormData();
    titleIds.forEach(id => formData.append('title_ids', id));
    return api.post('/bank/generate-remittance', formData);
  },
};

// Dashboard API
export const dashboardAPI = {
  getMetrics: () => api.get('/dashboard/metrics'),
};

// AI API
export const aiAPI = {
  analyzeDefaults: () => api.post('/ai/analyze-defaults'),
};

// Logs API
export const logsAPI = {
  list: (limit = 100) => api.get('/logs', { params: { limit } }),
};

// Config API
export const configAPI = {
  get: () => api.get('/config'),
  update: (data) => api.put('/config', data),
};

// Employees API
export const employeesAPI = {
  list: (params) => api.get('/employees', { params }),
  get: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  createAccess: (id) => api.post(`/employees/${id}/create-access`),
  getPaymentTable: (id) => api.get(`/employees/${id}/payment-table`),
  addPaymentTableEntry: (id, data) => api.post(`/employees/${id}/payment-table`, data),
  deletePaymentTableEntry: (entryId) => api.delete(`/employee-payment-tables/${entryId}`),
};

// Time Records API (Ponto)
export const timeRecordsAPI = {
  list: (params) => api.get('/time-records', { params }),
  create: (data) => api.post('/time-records', data),
  getDailyTimesheets: (params) => api.get('/daily-timesheets', { params }),
  updateTimesheet: (id, data) => api.put(`/daily-timesheets/${id}`, null, { params: data }),
  deleteTimesheet: (id) => api.delete(`/daily-timesheets/${id}`),
  getPayrollReport: (month) => api.get('/payroll-report', { params: { month } }),
};

// Teacher Portal API
export const teacherPortalAPI = {
  getMyClasses: () => api.get('/teacher-portal/my-classes'),
  getClassStudents: (classGroupId) => api.get(`/teacher-portal/class/${classGroupId}/students`),
  // Grades
  listGrades: (params) => api.get('/student-grades', { params }),
  createGrade: (data) => api.post('/student-grades', data),
  updateGrade: (id, gradeValue, notes) => api.put(`/student-grades/${id}`, null, { params: { grade_value: gradeValue, notes } }),
  deleteGrade: (id) => api.delete(`/student-grades/${id}`),
  // Attendance
  listAttendance: (params) => api.get('/student-attendance', { params }),
  createAttendance: (data) => api.post('/student-attendance', data),
  createBulkAttendance: (data) => api.post('/student-attendance/bulk', data),
  // Lesson Content
  listLessonContents: (params) => api.get('/lesson-contents', { params }),
  createLessonContent: (data) => api.post('/lesson-contents', data),
  updateLessonContent: (id, data) => api.put(`/lesson-contents/${id}`, null, { params: data }),
};

// Teacher Assignments API
export const teacherAssignmentsAPI = {
  list: (params) => api.get('/teacher-assignments', { params }),
  create: (data) => api.post('/teacher-assignments', data),
  delete: (id) => api.delete(`/teacher-assignments/${id}`),
};


// Financial API (Sistema Financeiro Completo)
export const financeiroAPI = {
  // Unidades
  listUnidades: () => api.get('/fin/unidades'),
  createUnidade: (data) => api.post('/fin/unidades', data),
  updateUnidade: (id, data) => api.put(`/fin/unidades/${id}`, data),
  deleteUnidade: (id) => api.delete(`/fin/unidades/${id}`),
  
  // Segmentos
  listSegmentos: () => api.get('/fin/segmentos'),
  createSegmento: (data) => api.post('/fin/segmentos', data),
  updateSegmento: (id, data) => api.put(`/fin/segmentos/${id}`, data),
  deleteSegmento: (id) => api.delete(`/fin/segmentos/${id}`),
  
  // Centros de Custo
  listCentrosCusto: () => api.get('/fin/centros-custo'),
  createCentroCusto: (data) => api.post('/fin/centros-custo', data),
  updateCentroCusto: (id, data) => api.put(`/fin/centros-custo/${id}`, data),
  deleteCentroCusto: (id) => api.delete(`/fin/centros-custo/${id}`),
  
  // Plano de Contas
  listPlanoContas: (tipo) => api.get('/fin/plano-contas', { params: { tipo } }),
  createPlanoConta: (data) => api.post('/fin/plano-contas', data),
  updatePlanoConta: (id, data) => api.put(`/fin/plano-contas/${id}`, data),
  deletePlanoConta: (id) => api.delete(`/fin/plano-contas/${id}`),
  
  // Lançamentos
  listLancamentos: (params) => api.get('/fin/lancamentos', { params }),
  createLancamento: (data) => api.post('/fin/lancamentos', data),
  updateLancamento: (id, data) => api.put(`/fin/lancamentos/${id}`, data),
  deleteLancamento: (id) => api.delete(`/fin/lancamentos/${id}`),
  
  // Base de Alunos
  listBaseAlunos: (competencia) => api.get('/fin/base-alunos', { params: { competencia } }),
  createBaseAlunos: (data) => api.post('/fin/base-alunos', data),
  deleteBaseAlunos: (id) => api.delete(`/fin/base-alunos/${id}`),
  
  // Base de Receita
  listBaseReceita: (competencia) => api.get('/fin/base-receita', { params: { competencia } }),
  createBaseReceita: (data) => api.post('/fin/base-receita', data),
  calcularBaseReceita: (competencia) => api.post(`/fin/base-receita/calcular?competencia=${competencia}`),
  
  // Regras de Rateio
  listRateioRegras: () => api.get('/fin/rateio-regras'),
  createRateioRegra: (data) => api.post('/fin/rateio-regras', data),
  updateRateioRegra: (id, data) => api.put(`/fin/rateio-regras/${id}`, data),
  deleteRateioRegra: (id) => api.delete(`/fin/rateio-regras/${id}`),
  
  // Rateio Input
  listRateioInput: (competencia) => api.get('/fin/rateio-input', { params: { competencia } }),
  createRateioInput: (data) => api.post('/fin/rateio-input', data),
  deleteRateioInput: (id) => api.delete(`/fin/rateio-input/${id}`),
  
  // Rateio Saída
  listRateioSaida: (competencia) => api.get('/fin/rateio-saida', { params: { competencia } }),
  processarRateio: (competencia, cod_unidade) => api.post(`/fin/processar-rateio?competencia=${competencia}&cod_unidade=${cod_unidade}`),
  limparRateio: (competencia, cod_unidade) => api.delete(`/fin/rateio-saida/limpar?competencia=${competencia}${cod_unidade ? `&cod_unidade=${cod_unidade}` : ''}`),
  
  // DRE
  getDRE: (params) => api.get('/fin/dre', { params }),
  getDREComparativo: (params) => api.get('/fin/dre/comparativo', { params }),
  exportDREExcel: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${api.defaults.baseURL}/fin/dre/export/excel?${queryString}`;
  },
  exportDREPDF: (params) => {
    const queryString = new URLSearchParams(params).toString();
    return `${api.defaults.baseURL}/fin/dre/export/pdf?${queryString}`;
  },
  
  // Seed
  seed: () => api.post('/fin/seed'),
};
